import { useCallback, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { funnelApi, initialSessionId, reportClientError, type Answer, type FunnelConfig } from '../lib/api'
import { useFunnelStore } from '../store/funnel'
import { consentSettled } from './consent'
import {
  firstNodeId,
  nextNodeId,
  previousNodeId,
  progressFor,
  stepNumberFor,
  type Answers,
  type FlowFlags,
} from './flow'

/** Node ids live at /f/:nodeId; the landing question owns the bare `/` URL. */
export function routeFor(nodeId: string): string {
  return nodeId === firstNodeId ? '/' : `/f/${nodeId}`
}

/**
 * Client-side mode override for QA (`?mode=paid` / `?mode=waitlist`). Presentation
 * only — the server never grants anything off a query parameter, so the worst a
 * visitor can do with this is preview the other funnel's copy.
 */
function modeOverride(): FunnelConfig['mode'] | null {
  const mode = new URLSearchParams(location.search).get('mode')
  return mode === 'paid' || mode === 'waitlist' ? mode : null
}

const FALLBACK_CONFIG: FunnelConfig = {
  mode: 'paid',
  deckOffer: { enabled: false, url: null, amount: null, currency: 'EUR' },
  appStoreUrl: null,
}

export function useFunnelConfig(): FunnelConfig {
  const query = useQuery({
    queryKey: ['funnel-config'],
    queryFn: funnelApi.config,
    staleTime: Infinity,
    retry: 1,
  })
  const override = modeOverride()
  const config = query.data ?? FALLBACK_CONFIG
  return override ? { ...config, mode: override } : config
}

export interface Funnel {
  answers: Answers
  flags: FlowFlags
  /** Persist an answer, then advance. Navigation never waits on the network. */
  answerAndAdvance: (nodeId: string, questionKey: string, answer: Answer) => void
  advance: (nodeId: string) => void
  goBack: (nodeId: string) => void
  progress: (nodeId: string) => number
  step: (nodeId: string) => { n: number; total: number }
}

export function useFunnel(): Funnel {
  const navigate = useNavigate()
  const answers = useFunnelStore((state) => state.answers)
  const setAnswer = useFunnelStore((state) => state.setAnswer)
  const setDirection = useFunnelStore((state) => state.setDirection)
  const researchConsent = useFunnelStore((state) => state.researchConsent)

  const config = useFunnelConfig()
  const flags = useMemo<FlowFlags>(
    () => ({
      // There is no separate research control any more — participation rides on
      // the Terms acceptance. So it defaults ON, and only a session the server
      // explicitly marked `false` (e.g. someone who withdrew in the app) drops
      // those four questions.
      research: researchConsent !== false,
      mode: config.mode,
    }),
    [researchConsent, config.mode]
  )

  // The progress bar must never travel backwards: a branch that shortens the
  // remaining path mid-run would otherwise make the user feel they lost ground.
  const highWaterMark = useRef(0)

  const advance = useCallback(
    (nodeId: string) => {
      setDirection('forward')
      navigate(routeFor(nextNodeId(nodeId, useFunnelStore.getState().answers, flags)))
    },
    [flags, navigate, setDirection]
  )

  const answerAndAdvance = useCallback(
    (nodeId: string, questionKey: string, answer: Answer) => {
      // Optimistic: the store updates first so the next screen already knows the
      // answer that decides its branch. The server write is fire-and-forget —
      // a slow network must never sit between a tap and the next question.
      //
      // It chains off consentSettled() because the API rejects every answer
      // until the landing agreement is on record, and the landing tap fires
      // both at once.
      setAnswer(questionKey, answer)
      void consentSettled()
        .then(() => funnelApi.answer(initialSessionId, questionKey, answer))
        .catch((error) => reportClientError(`answer:${questionKey}`, error))
      const next = nextNodeId(nodeId, { ...useFunnelStore.getState().answers, [questionKey]: answer }, flags)
      setDirection('forward')
      navigate(routeFor(next))
    },
    [flags, navigate, setAnswer, setDirection]
  )

  const goBack = useCallback(
    (nodeId: string) => {
      const previous = previousNodeId(nodeId, answers, flags)
      setDirection('back')
      if (previous) navigate(routeFor(previous))
      else navigate('/')
    },
    [answers, flags, navigate, setDirection]
  )

  const progress = useCallback(
    (nodeId: string) => {
      const value = progressFor(nodeId, answers, flags)
      highWaterMark.current = Math.max(highWaterMark.current, value)
      return highWaterMark.current
    },
    [answers, flags]
  )

  const step = useCallback((nodeId: string) => stepNumberFor(nodeId, answers, flags), [answers, flags])

  return { answers, flags, answerAndAdvance, advance, goBack, progress, step }
}
